/**
 * Input module. Use InputManager only. Keyboard, mouse, touch, joystick, gestures.
 * Public: connect, disconnect, update; isKeyDown/isKeyPressed, getMovementVector, getPressedKeys;
 * isMouseButtonDown/isMouseButtonPressed, getMousePosition, getMouseDelta, getMouseWheelDelta, isMouseDragging;
 * isTouchActive, getTouchCount, getTouchDelta, getPinchDelta; getJoystickVector, isJoystickActive;
 * getSwipeDirection, consumeSwipe; setCanvas, clearInputState.
 */

import type {
    InputManagerConfig,
    MousePosition,
    KeyboardState,
    MouseState,
    TouchState,
    TouchPoint,
    Vector2Like,
    SwipeDirection,
    SwipeData,
    JoystickConfig,
    JoystickState,
} from "./Input_Types";

import { MouseButton, KeyCode } from "./Input_Types";
import { KeyboardMouseManager } from "./KeyboardMouseManager";
import { TouchGestureManager } from "./TouchGestureManager";
import { JoystickManager } from "./JoystickManager";

export class InputManager {
    // Configuration
    private config: InputManagerConfig;

    // Specialized managers
    private keyboardMouse: KeyboardMouseManager;
    private touchGesture: TouchGestureManager;
    private joystick: JoystickManager;

    // Canvas reference for coordinate calculations
    private canvas: HTMLCanvasElement | null = null;
    private canvasRect: DOMRect | null = null;

    // Event listeners: (target, event, handler) so disconnect removes from correct target
    private eventListeners: Array<{ target: EventTarget; event: string; handler: EventListener; options?: AddEventListenerOptions }> = [];

    constructor(config: InputManagerConfig = {}) {
        this.config = {
            preventDefault: true,
            stopPropagation: false,
            enableTouch: true,
            ...config,
        };

        this.canvas = config.canvas || null;

        // Initialize all managers
        this.keyboardMouse = new KeyboardMouseManager();
        this.touchGesture = new TouchGestureManager();
        this.joystick = new JoystickManager();

        // Bind event handlers
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleKeyUp = this.handleKeyUp.bind(this);
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleWheel = this.handleWheel.bind(this);
        this.handleMouseEnter = this.handleMouseEnter.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.handleContextMenu = this.handleContextMenu.bind(this);

        // Cache canvas bounding rect
        this.updateCanvasRect();

        // Initialize joysticks from config
        if (config.joysticks) {
            config.joysticks.forEach(joystickConfig => {
                this.joystick.addJoystick(joystickConfig);
            });
        }
    }

    /** Attach event listeners (keyboard, mouse, touch, resize). Call once after creation. */
    connect(): void {
        this.disconnect();
        const target = this.config.target || document;

        // Keyboard
        this.addEventListener(target, "keydown", this.handleKeyDown as EventListener);
        this.addEventListener(target, "keyup", this.handleKeyUp as EventListener);

        // Mouse (wheel needs passive: false when we call preventDefault)
        this.addEventListener(target, "mousedown", this.handleMouseDown as EventListener);
        this.addEventListener(target, "mouseup", this.handleMouseUp as EventListener);
        this.addEventListener(target, "mousemove", this.handleMouseMove as EventListener);
        this.addEventListener(target, "wheel", this.handleWheel as EventListener, { passive: false });
        this.addEventListener(target, "mouseenter", this.handleMouseEnter as EventListener);
        this.addEventListener(target, "mouseleave", this.handleMouseLeave as EventListener);

        // Touch (passive: false so preventDefault works and no [Intervention] warning)
        if (this.config.enableTouch) {
            this.addEventListener(target, "touchstart", this.handleTouchStart as EventListener, { passive: false });
            this.addEventListener(target, "touchmove", this.handleTouchMove as EventListener, { passive: false });
            this.addEventListener(target, "touchend", this.handleTouchEnd as EventListener, { passive: false });
            this.addEventListener(target, "touchcancel", this.handleTouchEnd as EventListener, { passive: false });
        }

        // Window events
        this.addEventListener(window, "resize", this.handleResize as EventListener);
        this.addEventListener(window, "blur", this.handleBlur as EventListener);
        this.addEventListener(target, "contextmenu", this.handleContextMenu as EventListener);
    }

    /** Remove all listeners and cancel swipe. Call on teardown. */
    disconnect(): void {
        this.eventListeners.forEach(({ target, event, handler }) => {
            target.removeEventListener(event, handler);
        });
        this.eventListeners.length = 0;
        this.touchGesture.cancelSwipe();
    }

    /** Call each frame before reading input (deltaTime optional, for velocity). */
    update(deltaTime?: number): void {
        this.keyboardMouse.update(deltaTime);
        this.touchGesture.update();
    }

    /** True if key is held (use KeyCode or string e.g. "KeyW"). */
    isKeyDown(key: string): boolean {
        return this.keyboardMouse.isKeyDown(key);
    }

    isKeyUp(key: string): boolean {
        return this.keyboardMouse.isKeyUp(key);
    }

    /** True only on the frame key went down (one-shot, not held). */
    isKeyPressed(key: string): boolean {
        return this.keyboardMouse.isKeyPressed(key);
    }

    isAnyKeyDown(keys: string[]): boolean {
        return this.keyboardMouse.isAnyKeyDown(keys);
    }

    areAllKeysDown(keys: string[]): boolean {
        return this.keyboardMouse.areAllKeysDown(keys);
    }

    /** WASD/Arrow → { x, y } in -1..1. */
    getMovementVector(): Vector2Like {
        return this.keyboardMouse.getMovementVector();
    }

    getKeyboardState(): Readonly<KeyboardState> {
        return this.keyboardMouse.getKeyboardState();
    }

    getPressedKeys(): string[] {
        return this.keyboardMouse.getPressedKeys();
    }

    getLastPressedKey(): string | null {
        return this.keyboardMouse.getLastPressedKey();
    }

    /** True if mouse button is held (MouseButton.LEFT/MIDDLE/RIGHT). */
    isMouseButtonDown(button: MouseButton): boolean {
        return this.keyboardMouse.isMouseButtonDown(button);
    }

    /** True only on the frame button went down (one-shot, not held). */
    isMouseButtonPressed(button: MouseButton): boolean {
        return this.keyboardMouse.isMouseButtonPressed(button);
    }

    /** Mouse position (screen, canvas, normalized -1..1). */
    getMousePosition(): MousePosition {
        return this.keyboardMouse.getMousePosition();
    }

    /** Normalized mouse (-1..1, center origin). */
    getNormalizedMousePosition(): Vector2Like {
        return this.keyboardMouse.getMousePosition().normalized;
    }

    getMouseWheelDelta(): Vector2Like {
        return this.keyboardMouse.getMouseWheelDelta();
    }

    isMouseOverCanvas(): boolean {
        return this.keyboardMouse.isMouseOverCanvas();
    }

    getMouseState(): Readonly<MouseState> {
        return this.keyboardMouse.getMouseState();
    }

    /** Mouse delta since last frame (pixels; works with pointer lock). */
    getMouseDelta(): Vector2Like {
        return this.keyboardMouse.getMouseDelta();
    }

    getMouseVelocity(): Vector2Like {
        return this.keyboardMouse.getMouseVelocity();
    }

    /** True if dragging (optional button filter). */
    isMouseDragging(button?: MouseButton): boolean {
        return this.keyboardMouse.isMouseDragging(button);
    }

    getDragStartPosition(): MousePosition | null {
        return this.keyboardMouse.getDragStartPosition();
    }

    getDragDelta(): Vector2Like {
        return this.keyboardMouse.getDragDelta();
    }

    // ===== TOUCH =====

    /** True if any touch is currently active. */
    isTouchActive(): boolean {
        return this.touchGesture.isTouchActive();
    }

    /** Number of fingers currently touching the screen. */
    getTouchCount(): number {
        return this.touchGesture.getTouchCount();
    }

    /** Position of first active touch (canvas coords). */
    getTouchPosition(): Vector2Like {
        return this.touchGesture.getTouchPosition();
    }

    /** True if tap just occurred (touch down+up within 300ms). */
    isTap(): boolean {
        return this.touchGesture.isTap();
    }

    /** All active touch points (id + position). */
    getAllTouches(): TouchPoint[] {
        return this.touchGesture.getAllTouches();
    }

    /** Readonly touch state. */
    getTouchState(): Readonly<TouchState> {
        return this.touchGesture.getTouchState();
    }

    /** Current distance between two touch points (pixels). */
    getPinchDistance(): number {
        return this.touchGesture.getPinchDistance();
    }

    /** Pinch delta since last frame (positive = zoom in, negative = zoom out, pixels). */
    getPinchDelta(): number {
        return this.touchGesture.getPinchDelta();
    }

    /** True if two-finger pinch is active. */
    isPinching(): boolean {
        return this.touchGesture.isPinching();
    }

    /** Center point between all active touches (canvas coords). */
    getTouchCenter(): Vector2Like {
        return this.touchGesture.getTouchCenter();
    }

    /** Touch movement delta since last frame (pixels). */
    getTouchDelta(): Vector2Like {
        return this.touchGesture.getTouchDelta();
    }

    // ===== GESTURES =====

    /** Current swipe direction (8-way) or null. */
    getSwipeDirection(): SwipeDirection | null {
        return this.touchGesture.getSwipeDirection();
    }

    /** Current swipe distance (pixels). */
    getSwipeDistance(): number {
        return this.touchGesture.getSwipeDistance();
    }

    /** Full swipe data (direction, distance, positions, duration) or null. */
    getSwipeData(): SwipeData | null {
        return this.touchGesture.getSwipeData();
    }

    /** True if current swipe matches the given direction. */
    isSwipeDirection(direction: SwipeDirection): boolean {
        return this.touchGesture.isSwipeDirection(direction);
    }

    /** True if swiped left. */
    isSwipeLeft(): boolean {
        return this.touchGesture.isSwipeDirection("left");
    }

    /** True if swiped right. */
    isSwipeRight(): boolean {
        return this.touchGesture.isSwipeDirection("right");
    }

    /** True if swiped up. */
    isSwipeUp(): boolean {
        return this.touchGesture.isSwipeDirection("up");
    }

    /** True if swiped down. */
    isSwipeDown(): boolean {
        return this.touchGesture.isSwipeDirection("down");
    }

    /** True if swiped up-left. */
    isSwipeUpLeft(): boolean {
        return this.touchGesture.isSwipeDirection("up-left");
    }

    /** True if swiped up-right. */
    isSwipeUpRight(): boolean {
        return this.touchGesture.isSwipeDirection("up-right");
    }

    /** True if swiped down-left. */
    isSwipeDownLeft(): boolean {
        return this.touchGesture.isSwipeDirection("down-left");
    }

    /** True if swiped down-right. */
    isSwipeDownRight(): boolean {
        return this.touchGesture.isSwipeDirection("down-right");
    }

    /** True if any swipe was detected. */
    isAnySwipe(): boolean {
        return this.touchGesture.getSwipeDirection() !== null;
    }

    /** Set minimum swipe distance (pixels). */
    setSwipeThreshold(threshold: number): void {
        this.touchGesture.setSwipeThreshold(threshold);
    }

    /** Current swipe threshold (pixels). */
    getSwipeThreshold(): number {
        return this.touchGesture.getSwipeThreshold();
    }

    /** Get swipe data and clear it (one-time consumption). */
    consumeSwipe(): SwipeData | null {
        return this.touchGesture.consumeSwipe();
    }

    // ===== JOYSTICK =====

    /** Add a virtual joystick at runtime. */
    addJoystick(config: JoystickConfig): void {
        this.joystick.addJoystick(config);
    }

    /** Remove a virtual joystick by id. */
    removeJoystick(id: string): void {
        this.joystick.removeJoystick(id);
    }

    /** Full joystick state by id, or null. */
    getJoystick(id: string): JoystickState | null {
        return this.joystick.getJoystick(id);
    }

    /** Normalized joystick vector (deadzone applied), -1..1. */
    getJoystickVector(id: string): Vector2Like {
        return this.joystick.getJoystickVector(id);
    }

    /** Joystick angle in radians (-π..π). */
    getJoystickAngle(id: string): number {
        return this.joystick.getJoystickAngle(id);
    }

    /** Normalized distance from center (0..1, deadzone applied). */
    getJoystickDistance(id: string): number {
        return this.joystick.getJoystickDistance(id);
    }

    /** True if joystick is currently being touched. */
    isJoystickActive(id: string): boolean {
        return this.joystick.isJoystickActive(id);
    }

    /** All registered joystick ids. */
    getJoystickIds(): string[] {
        return this.joystick.getJoystickIds();
    }

    /** Reset joystick to center. */
    resetJoystick(id: string): void {
        this.joystick.resetJoystick(id);
    }

    // ===== UTILITY =====

    /** Set canvas for coordinate calculations. */
    setCanvas(canvas: HTMLCanvasElement | null): void {
        this.canvas = canvas;
        this.updateCanvasRect();
    }

    /** Clear all input state (keys, mouse, touch, joysticks). Use on blur/pause. */
    clearInputState(): void {
        this.keyboardMouse.clear();
        this.touchGesture.clear();
        this.joystick.clearAll();
    }

    // ===== EVENT HANDLERS =====

    private addEventListener(target: EventTarget, event: string, handler: EventListener, options?: AddEventListenerOptions): void {
        target.addEventListener(event, handler, options);
        this.eventListeners.push({ target, event, handler, options });
    }

    private handleKeyDown(event: KeyboardEvent): void {
        if (this.config?.preventDefault) event.preventDefault();
        if (this.config?.stopPropagation) event.stopPropagation();

        this.keyboardMouse.handleKeyDown(event.code);
    }

    private handleKeyUp(event: KeyboardEvent): void {
        if (this.config?.preventDefault) event.preventDefault();
        if (this.config?.stopPropagation) event.stopPropagation();

        this.keyboardMouse.handleKeyUp(event.code);
    }

    private handleMouseDown(event: MouseEvent): void {
        if (this.config?.preventDefault) event.preventDefault();
        if (this.config?.stopPropagation) event.stopPropagation();

        this.updateMousePosition(event);
        this.keyboardMouse.handleMouseButtonDown(event.button);

        // Start swipe on left button
        if (event.button === 0) {
            const mousePos = this.keyboardMouse.getMousePosition();
            this.touchGesture.startSwipe(mousePos.canvas);
        }
    }

    private handleMouseUp(event: MouseEvent): void {
        if (this.config?.preventDefault) event.preventDefault();
        if (this.config?.stopPropagation) event.stopPropagation();

        this.updateMousePosition(event);
        this.keyboardMouse.handleMouseButtonUp(event.button);

        // End swipe on left button
        if (event.button === 0) {
            const mousePos = this.keyboardMouse.getMousePosition();
            this.touchGesture.endSwipe(mousePos.canvas);
        }
    }

    private handleMouseMove(event: MouseEvent): void {
        if (this.config?.preventDefault) event.preventDefault();
        if (this.config?.stopPropagation) event.stopPropagation();

        // Check if pointer lock is active
        if (document.pointerLockElement) {
            // Use movementX/movementY for pointer lock
            this.keyboardMouse.handlePointerLockMove(event.movementX, event.movementY);
        } else {
            // Normal mouse position tracking
            this.updateMousePosition(event);
        }

        this.keyboardMouse.handleMouseMove();

        // Update swipe
        const mousePos = this.keyboardMouse.getMousePosition();
        this.touchGesture.updateSwipe(mousePos.canvas);
    }

    private handleWheel(event: WheelEvent): void {
        if (this.config?.preventDefault) event.preventDefault();
        if (this.config?.stopPropagation) event.stopPropagation();

        this.keyboardMouse.handleMouseWheel(event.deltaX, event.deltaY);
    }

    private handleMouseEnter(): void {
        this.keyboardMouse.setMouseOverCanvas(true);
    }

    private handleMouseLeave(): void {
        this.keyboardMouse.setMouseOverCanvas(false);
        this.touchGesture.cancelSwipe();
    }

    private handleTouchStart(event: TouchEvent): void {
        if (this.config?.preventDefault) event.preventDefault();
        if (this.config?.stopPropagation) event.stopPropagation();

        // Handle joystick touches first - only process new touches (changedTouches)
        let joystickHandled = false;
        for (let i = 0; i < event.changedTouches.length; i++) {
            if (this.joystick.handleTouchStart(event.changedTouches[i], this.getTouchPositionFromTouch.bind(this))) {
                joystickHandled = true;
            }
        }

        // If touch was handled by joystick, don't process as regular touch
        if (joystickHandled && event.changedTouches.length === 1) {
            return;
        }

        // Collect non-joystick touches from all active touches
        const nonJoystickTouches: TouchPoint[] = [];

        for (let i = 0; i < event.touches.length; i++) {
            const touch = event.touches[i];

            if (!this.joystick.isTouchUsedByJoystick(touch.identifier)) {
                const position = this.getTouchPositionFromTouch(touch);
                nonJoystickTouches.push({
                    id: touch.identifier,
                    position,
                    force: touch.force || undefined,
                });
            }
        }

        this.touchGesture.startTouch(nonJoystickTouches);

        // Start swipe for first non-joystick touch
        if (nonJoystickTouches.length > 0) {
            this.touchGesture.startSwipe(nonJoystickTouches[0].position.canvas);
        }
    }

    private handleTouchMove(event: TouchEvent): void {
        if (this.config?.preventDefault) event.preventDefault();
        if (this.config?.stopPropagation) event.stopPropagation();

        // Handle joystick touches
        for (let i = 0; i < event.touches.length; i++) {
            this.joystick.handleTouchMove(event.touches[i], this.getTouchPositionFromTouch.bind(this));
        }

        // Collect non-joystick touches
        const nonJoystickTouches: TouchPoint[] = [];

        for (let i = 0; i < event.touches.length; i++) {
            const touch = event.touches[i];

            if (!this.joystick.isTouchUsedByJoystick(touch.identifier)) {
                const position = this.getTouchPositionFromTouch(touch);
                nonJoystickTouches.push({
                    id: touch.identifier,
                    position,
                    force: touch.force || undefined,
                });
            }
        }

        this.touchGesture.updateTouchPositions(nonJoystickTouches);

        // Update swipe
        if (nonJoystickTouches.length > 0) {
            this.touchGesture.updateSwipe(nonJoystickTouches[0].position.canvas);
        }
    }

    private handleTouchEnd(event: TouchEvent): void {
        if (this.config?.preventDefault) event.preventDefault();
        if (this.config?.stopPropagation) event.stopPropagation();

        // Handle joystick touch end
        for (let i = 0; i < event.changedTouches.length; i++) {
            this.joystick.handleTouchEnd(event.changedTouches[i].identifier);
        }

        // Finalize touch/swipe for non-joystick touches
        if (event.changedTouches.length > 0) {
            const wasJoystickTouch = this.joystick.isTouchUsedByJoystick(event.changedTouches[0].identifier);

            if (!wasJoystickTouch) {
                const touchPos = this.getTouchPositionFromTouch(event.changedTouches[0]);
                this.touchGesture.endTouch(touchPos.canvas);
            } else {
                this.touchGesture.endTouch();
            }
        } else {
            this.touchGesture.endTouch();
        }
    }

    private handleResize(): void {
        this.updateCanvasRect();
    }

    private handleBlur(): void {
        this.clearInputState();
    }

    private handleContextMenu(event: Event): void {
        event.preventDefault();
        if (this.config?.stopPropagation) {
            event.stopPropagation();
        }
    }

    // ===== PRIVATE UTILITIES =====

    private updateMousePosition(event: MouseEvent): void {
        const screenPos = { x: event.clientX, y: event.clientY };
        const canvasPos = this.getCanvasPosition(screenPos);
        const normalizedPos = this.getNormalizedPosition(canvasPos);

        this.keyboardMouse.setMousePosition({
            screen: screenPos,
            canvas: canvasPos,
            normalized: normalizedPos,
        });
    }

    private getTouchPositionFromTouch(touch: Touch): MousePosition {
        const screenPos = { x: touch.clientX, y: touch.clientY };
        const canvasPos = this.getCanvasPosition(screenPos);
        const normalizedPos = this.getNormalizedPosition(canvasPos);

        return {
            screen: screenPos,
            canvas: canvasPos,
            normalized: normalizedPos,
        };
    }

    private getCanvasPosition(screenPos: Vector2Like): Vector2Like {
        if (!this.canvas || !this.canvasRect) {
            return { x: screenPos.x, y: screenPos.y };
        }

        return {
            x: screenPos.x - this.canvasRect.left,
            y: screenPos.y - this.canvasRect.top,
        };
    }

    private getNormalizedPosition(canvasPos: Vector2Like): Vector2Like {
        if (!this.canvas || !this.canvasRect) {
            return { x: 0, y: 0 };
        }

        const x = (canvasPos.x / this.canvasRect.width) * 2 - 1;
        const y = -(canvasPos.y / this.canvasRect.height) * 2 + 1;

        return { x, y };
    }

    private updateCanvasRect(): void {
        if (this.canvas) {
            this.canvasRect = this.canvas.getBoundingClientRect();
        } else {
            this.canvasRect = null;
        }
    }
}

// Re-export KeyCode for convenience
export { KeyCode };
